import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2, Loader2 } from "lucide-react";
import { company } from "../mock/mock";
import { PageHero } from "../components/Common";
import { useToast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const offices = [
  { name: "Operations Center - India", org: "Midatlantic Business Management Pvt Ltd", addr: "711, 7th floor, Stellar Spaces, Kharadi, Pune, MH 411014 India", phone: "+91 20-35899759" },
  { name: "Operations Center 2 - Nagaland", org: "Midatlantic Business Management Pvt Ltd", addr: "Kohima STPI, Directorate of Information Technology & Communication, Thizama Road, Kohima, Nagaland - 797001", phone: "+91 904-964-1279" },
];

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", subscribe: false });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast({ title: "Missing information", description: "Please fill in name, email and phone." });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast({ title: "Message sent!", description: "Our Client Engagement Team will contact you within 24 hours." });
      setForm({ name: "", email: "", phone: "", message: "", subscribe: false });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast({ title: "Too many submissions", description: "Please try again in a little while." });
      } else {
        toast({ title: "Something went wrong", description: "Please try again or call us directly." });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Toaster />
      <PageHero
        eyebrow="Contact Us"
        title="Speak with one of our advisors"
        subtitle="Fill in the inquiry form and our Client Engagement Team will contact you within 24 hours. Book your free consultation — no obligation to sign."
      />

      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14">
          {/* Form */}
          <div className="bg-secondary rounded-2xl p-8 md:p-10 border border-border">
            {submitted ? (
              <div className="text-center py-16">
                <CheckCircle2 size={56} className="text-gold mx-auto mb-5" />
                <h3 className="font-heading font-bold text-navy text-2xl mb-2">Thank you!</h3>
                <p className="text-muted-foreground">Your message has been received. We'll be in touch within 24 hours.</p>
                <button onClick={() => setSubmitted(false)} className="mt-6 rounded-md bg-navy px-6 py-3 text-sm font-bold text-white hover:bg-navy-light transition-colors">
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <h3 className="font-heading font-bold text-navy text-xl">Get your free consultation</h3>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-1.5">Name</label>
                  <input name="name" value={form.name} onChange={handleChange} className="w-full rounded-md border border-border bg-white px-4 py-3 text-sm outline-none focus:border-navy-light focus:ring-2 focus:ring-navy-light/20 transition" placeholder="Your name" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-1.5">Email *</label>
                  <input name="email" type="email" value={form.email} onChange={handleChange} className="w-full rounded-md border border-border bg-white px-4 py-3 text-sm outline-none focus:border-navy-light focus:ring-2 focus:ring-navy-light/20 transition" placeholder="you@company.com" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-1.5">Phone *</label>
                  <input name="phone" value={form.phone} onChange={handleChange} className="w-full rounded-md border border-border bg-white px-4 py-3 text-sm outline-none focus:border-navy-light focus:ring-2 focus:ring-navy-light/20 transition" placeholder="+1 (000) 000-0000" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-1.5">How can we help?</label>
                  <textarea name="message" value={form.message} onChange={handleChange} rows={4} className="w-full rounded-md border border-border bg-white px-4 py-3 text-sm outline-none focus:border-navy-light focus:ring-2 focus:ring-navy-light/20 transition resize-none" placeholder="Tell us about your needs" />
                </div>
                <label className="flex items-start gap-3 text-sm text-muted-foreground cursor-pointer">
                  <input type="checkbox" name="subscribe" checked={form.subscribe} onChange={handleChange} className="mt-1 accent-navy" />
                  <span>Sign up for our email list for updates, promotions, and more.</span>
                </label>
                <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-gold py-3.5 text-sm font-bold text-navy-dark hover:bg-gold-light transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                  {loading ? (<><Loader2 size={16} className="animate-spin" /> Sending...</>) : (<><Send size={16} /> Send Message</>)}
                </button>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  By providing my phone number, I agree that Mid Atlantic Business Management may send text messages for any purpose. Message and data rates may apply. Reply STOP to opt-out.
                </p>
              </form>
            )}
          </div>

          {/* Info */}
          <div>
            <h3 className="font-heading font-bold text-navy text-2xl mb-6">Mid Atlantic Business Management Inc</h3>
            <div className="space-y-5 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg brand-gradient flex items-center justify-center shrink-0"><MapPin size={18} className="text-gold-light" /></div>
                <div><div className="font-semibold text-navy text-sm">Head Office</div><p className="text-sm text-muted-foreground">{company.addressUS}</p></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg brand-gradient flex items-center justify-center shrink-0"><Phone size={18} className="text-gold-light" /></div>
                <div><div className="font-semibold text-navy text-sm">Phone</div><a href={`tel:${company.phoneRaw}`} className="text-sm text-muted-foreground hover:text-navy">{company.phone}</a></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg brand-gradient flex items-center justify-center shrink-0"><Mail size={18} className="text-gold-light" /></div>
                <div><div className="font-semibold text-navy text-sm">Email</div><a href={`mailto:${company.email}`} className="text-sm text-muted-foreground hover:text-navy">{company.email}</a></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg brand-gradient flex items-center justify-center shrink-0"><Clock size={18} className="text-gold-light" /></div>
                <div><div className="font-semibold text-navy text-sm">Hours</div><p className="text-sm text-muted-foreground">Open today 09:00 am – 05:00 pm</p></div>
              </div>
            </div>

            <h4 className="font-heading font-bold text-navy text-lg mb-4">Our Offices</h4>
            <div className="space-y-4">
              {offices.map((o, i) => (
                <div key={i} className="rounded-xl border border-border p-5 card-lift">
                  <div className="section-eyebrow text-gold-dark mb-1">{o.name}</div>
                  <div className="font-semibold text-navy text-sm">{o.org}</div>
                  <p className="text-sm text-muted-foreground mt-1">{o.addr}</p>
                  <p className="text-sm text-muted-foreground">{o.phone}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
